import { TriggerSource, TriggerType } from 'utils/api/modules/alarms/alarms.constants';
import { AmberAlertActivityStatus, RedAlertActivityStatus } from 'utils/stores/Activities/constants';

import { env } from 'config';

import routes from 'core/routes';

export enum ProtectorType {
  ShortText = 'short_text',
  LongText = 'long_text',
  SingleChoice = 'single_choice',
  MultiChoice = 'multiple_choice',
  Date = 'date',
  User = 'user',
  Location = 'location',
  Number = 'number',
  DateTime = 'date_time',
  Time = 'time',
  Image = 'image',
  Section = 'section',
  Lookup = 'lookup',
  RiskFactor = 'risk_factor',
  Pipeline = 'pipeline',
  Stage = 'stage',
  Attachment = 'attachment',
  Signature = 'signature',
  RepeatingGroup = 'repeating_group',
  YesNoNa = 'yes_no_na',
}

export enum RepeatingGroupVariant {
  Procedures = 'procedures',
  Staff = 'staff',
}

export enum EntityModules {
  Assets = 'assets',
  Sites = 'sites',
  Contacts = 'contacts',
  RisksAssessments = 'riskAssessments',
  Events = 'events',
  Documents = 'documents',
  Risks = 'risks',
  Audits = 'audits',
  Controls = 'controls',
  Hazards = 'hazards',
}

export const LIBRARY_ENTITY_MODULES: readonly EntityModules[] = [EntityModules.Controls, EntityModules.Hazards];

// Entity modules with the uniform `details` + `related*` route shape. Audits has `related*` but no
// `details` — its record lives at /audits/forms/:formId — and the library modules live under /risks/
// with no `related*` routes, so only those are special-cased.
export type RegisterEntityModule = Exclude<
  EntityModules,
  EntityModules.Audits | EntityModules.Controls | EntityModules.Hazards
>;

// Runtime counterpart of RegisterEntityModule — `reverse()` would throw for the excluded modules.
export const isRegisterEntityModule = (module: unknown): module is RegisterEntityModule =>
  module !== EntityModules.Audits &&
  module !== EntityModules.Controls &&
  module !== EntityModules.Hazards &&
  Object.values(EntityModules).includes(module as EntityModules);

export enum NotificationTarget {
  NEW_TASK_ASSIGNED_TO_ME = 'new_task_assigned_to_me',
  NEW_INCIDENT_MESSAGE = 'incident_comment_mention',
  NEW_INCIDENT_SUBMISSION_MESSAGE = 'incident_submission_comment_mention',
  NEW_INCIDENT_REPORTED = 'incident_has_been_reported',
  NEW_INCIDENT_ASSIGNED_TO_ME = 'new_incident_assigned_to_me',
  NEW_TASK_MESSAGE = 'task_comment_mention',
  NEW_FORM_SUBMITTED = 'new_form_submitted',
  AUDIT_COMMENT_MENTION = 'audit_comment_mention',
  NEW_EVENT_REPORTED = 'event_has_been_reported',
  NEW_EVENT_ASSIGNED_TO_ME = 'new_event_assigned_to_me',
  NEW_EVENT_MESSAGE = 'event_comment_mention',
  NEW_ASSET_MESSAGE = 'asset_comment_mention',
  NEW_SITE_MESSAGE = 'site_comment_mention',
  NEW_SITE_ASSIGNED_TO_ME = 'site_assigned_to_me',
  EVENT_COLLABORATED = 'event_collaborated',
  EVENT_COMMENT = 'event_comment',
  EVENT_STAGE_CHANGED = 'event_stage_changed',
  STATUS_UPDATE_OF_MY_TASK = 'status_update_of_my_task',
  NEW_COMMENT_IN_MY_TASK = 'new_comment_in_my_task',
  NEW_SCHEDULE_ASSIGNED_TO_ME = 'new_schedule_assigned_to_me',
  ACKNOWLEDGEMENT_ASSIGNED_TO_ME = 'acknowledgement_assigned_to_me',
  DOCUMENT_COMMENT_MENTION = 'document_comment_mention',
  RISK_COMMENT_MENTION = 'risk_comment_mention',
  TASK_DUE_TODAY = 'task_due_today',
  AUDIT_DUE_TODAY = 'audit_due_today',
  ACKNOWLEDGEMENT_DUE_TODAY = 'acknowledgement_due_today',
  RISK_ACTION_ASSIGNED_TO_ME = 'risk_action_assigned_to_me',
  RISK_ACTION_DUE_TODAY = 'risk_action_due_today',
}

export enum UserRole {
  User = 'user',
  Manager = 'manager',
  AccountManager = 'account_manager',
  ArcOperator = 'arc_operator',
  Admin = 'admin',
}

export const UserRoleNames: Record<string, string> = Object.freeze({
  manager: 'Manager',
  account_manager: 'Account Owner',
  account_owner: 'Account Owner',
  user: 'User',
  admin: 'Admin',
  arc_operator: 'ARC Operator',
});

export interface ProductLicense {
  key: string;
  label: string;
}

export const PRODUCT_LICENSES: ReadonlyArray<ProductLicense> = Object.freeze([
  { key: 'protectorAlarms', label: 'Lone Working' },
  { key: 'events', label: 'Events' },
  { key: 'workflows', label: 'Audits' },
  { key: 'documents', label: 'Documents' },
  { key: 'risks', label: 'Risk Register' },
]);

// Events and Risk Register are hidden unless the organisation holds seats of that type; Lone
// Working, Audits and Documents are generally available and so are always listed.
export const productLicensesFor = ({
  hasEventsLicense,
  hasRisksLicense,
}: {
  hasEventsLicense: boolean;
  hasRisksLicense: boolean;
}): ReadonlyArray<ProductLicense> =>
  PRODUCT_LICENSES.filter(({ key }) => (hasEventsLicense || key !== 'events') && (hasRisksLicense || key !== 'risks'));

export const ProductLicenseName: Record<string, string> = Object.freeze(
  Object.fromEntries(PRODUCT_LICENSES.map(({ key, label }) => [key, label]))
);

export enum LicenseType {
  ProtectorMonitoringSelf = 'protector_monitoring_self',
  ProtectorMonitoringVatix = 'protector_monitoring_vatix',
}

export const LicenseTypeName = Object.freeze({
  [LicenseType.ProtectorMonitoringSelf]: 'Self Monitoring',
  [LicenseType.ProtectorMonitoringVatix]: 'Vatix 24/7 Monitoring',
});

export enum AlertStatus {
  RedAlert = 'red_alert',
  AmberAlert = 'amber_alert',
  NoAlert = 'no_alert',
}

export enum AssignmentStatus {
  Assigned = 'assigned',
  Unassigned = 'unassigned',
}

export const TriggerTypeName = Object.freeze({
  [TriggerType.Button]: 'SOS Pressed',
  [TriggerType.ExpiredTimer]: 'Timed Alert Expired',
});

export const TriggerSourceName: Record<TriggerSource, string> = Object.freeze({
  [TriggerSource.Button]: 'SOS Pressed',
  [TriggerSource.FallDetection]: 'Fall Detection',
  [TriggerSource.Discreet]: 'Discreet Alarm',
  [TriggerSource.AmberExpired]: 'Timed Alert Expired',
});

export const GOOGLE_MAPS_DEFAULT_POSITION = {
  position: {
    lat: 51.510033,
    lng: -0.108169,
  },
};

export const GOOGLE_MAPS_STYLES: google.maps.MapTypeStyle[] = [
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [
      {
        saturation: -100,
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'labels.text',
    stylers: [
      {
        color: '#545454',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      {
        saturation: -87,
      },
      {
        lightness: -40,
      },
      {
        color: '#ffffff',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#f0f0f0',
      },
      {
        saturation: -22,
      },
      {
        lightness: -16,
      },
    ],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [
      {
        saturation: -52,
      },
      {
        hue: '#00e4ff',
      },
      {
        lightness: -16,
      },
    ],
  },
];

export enum RedAlertStatus {
  Pending = 'pending',
  Accepted = 'accepted',
  Completed = 'completed',
}

export enum IncidentsStatus {
  Open = 'open',
  Resolved = 'resolved',
  Archived = 'archived',
}

export enum AmberAlertStatus {
  Pending = 'pending',
  Cancelled = 'cancelled',
  Finished = 'finished',
}

export const AmberAlertStatusName = Object.freeze({
  [AmberAlertStatus.Pending]: 'In progress',
  [AmberAlertStatus.Cancelled]: 'Closed by user',
  [AmberAlertStatus.Finished]: 'Alarm raised',
});

export const AmberAlertActivityStatusName = Object.freeze({
  [AmberAlertActivityStatus.Started]: 'started',
  [AmberAlertActivityStatus.Cancelled]: 'cancelled',
  [AmberAlertActivityStatus.Finished]: 'finished',
});

export const RedAlertActivityStatusName = Object.freeze({
  [RedAlertActivityStatus.Started]: 'started',
  [RedAlertActivityStatus.Accepted]: 'accepted',
  [RedAlertActivityStatus.Completed]: 'completed',
  [RedAlertActivityStatus.Cancelled]: 'cancelled',
});

export enum TaskState {
  Created = 'created',
  Deleted = 'deleted',
}

export enum EntityState {
  Created = 'created',
  Deleted = 'deleted',
}

export enum HeightUnit {
  Meter = 'm',
  FeetInch = 'ft.in',
}

export enum WeightUnit {
  Kilogram = 'kg',
  Pound = 'lb',
  Stone = 'st',
}

export enum Gender {
  Male = 'male',
  Female = 'female',
  Other = 'other',
}

export const GenderName = Object.freeze({
  [Gender.Male]: 'Male',
  [Gender.Female]: 'Female',
  [Gender.Other]: 'Other',
});

export enum MonitoringMethod {
  ArcMonitored = 'arc_monitored',
  SelfMonitored = 'self_monitored',
}

export const ESCALATION_TYPE_LABELS = Object.freeze({
  [MonitoringMethod.SelfMonitored]: 'Self-Monitoring',
  [MonitoringMethod.ArcMonitored]: 'Vatix 24/7 Monitoring',
  not_monitored: 'Not Monitored',
});

export const PAGE_SIZE = 15;

export const LOCATION_API_REFRESH_INTERVAL = 10000;

export const LOCATION_HISTORY_PERIOD_LATEST = '0';

export const LOCATION_HISTORY_PERIOD = Object.freeze({
  [LOCATION_HISTORY_PERIOD_LATEST]: 'Most recent',
  '1': '1 hour',
  '3': '3 hours',
  '6': '6 hours',
  '12': '12 hours',
  '24': '24 hours',
});

export const DEFAULT_SEARCH_DEBOUNCE = 700;

export const DEFAULT_FULL_DATE_FORMAT = 'YYYY-MM-DD';

export const DOTS_FULL_DATE_FORMAT = 'DD.MM.YYYY';

export const SLASH_FULL_DATE_FORMAT = 'DD/MM/YYYY';

export const DEFAULT_DATE_FORMAT = 'Do MMM YYYY';

export const DEFAULT_TIME_FORMAT = 'HH:mm';

export const FULL_DATETIME_FORMAT = `${DEFAULT_DATE_FORMAT}, ${DEFAULT_TIME_FORMAT}`;

export const SLASH_FULL_DATETIME_FORMAT = `${SLASH_FULL_DATE_FORMAT}, ${DEFAULT_TIME_FORMAT}`;

export const DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR = 'DD MMM YYYY, HH:mm';

// A grid column's floor for each moment pattern: its widest value with a `GMT-3:30` label in 14px Roboto,
// plus cell padding. DATETIME_ fits DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, FULL_DATETIME_ fits FULL_DATETIME_FORMAT.
export const DATETIME_COLUMN_MIN_WIDTH = 210;

export const FULL_DATETIME_COLUMN_MIN_WIDTH = 230;

// Keep in sync with core-api's DEFAULT_TIMEZONE.
export const DEFAULT_TIMEZONE = 'Europe/London';

// Separate from DEFAULT_TIMEZONE on purpose: the epoch date and time rule can change on its own.
export const FLOATING_TIMEZONE = 'Europe/London';

export const DEVICE_TIMEZONE_HEADER = 'X-Device-Timezone';

export const BACKGROUND_REQUEST_HEADER = 'X-Background-Request';
export const BACKGROUND_REQUEST_VALUE = 'true';

export const ACTIVITY_PING_INTERVAL_MS = 4 * 60 * 1000;

export const READER_TIMEZONE_HEADER = 'x-reader-timezone';

export const FOLDED_MAP_HEIGHT = 300;

export const Products = {
  protector: env.REACT_APP_PROTECTOR_UI_URL as string,
  admin: env.REACT_APP_ADMIN_UI_URL as string,
  workflows: env.REACT_APP_WORKFLOWS_UI_URL as string,
};

export const USER_TAG_BASE = String(routes.dashboard.settings.users);

export const SUPPORTED_ACTIVITIES_TYPES = [
  'image/png',
  'image/jpeg',
  'image/heic',
  'image/heif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/csv',
  'video/mp4',
  'audio/mpeg',
  'video/quicktime',
  'message/rfc822',
  'application/vnd.ms-outlook',
  'application/octet-stream',
];

// File extensions to allow when browser MIME type detection is unreliable
export const SUPPORTED_ACTIVITIES_EXTENSIONS = ['.msg'];

export const DEFAULT_FILE_UPLOAD_MAX_SIZE = 25 * 1024 * 1024;

export const MAX_IMPORT_DOCUMENT_SIZE = 10 * 1024 * 1024;

export const MAX_IMPORT_DOCUMENT_SIZE_LABEL = `${MAX_IMPORT_DOCUMENT_SIZE / 1024 / 1024} MB`;

export const MAX_LOGO_SIZE = 2 * 1024 * 1024;

export const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

export enum EntitiesStatus {
  Open = 'open',
  Resolved = 'resolved',
  Archived = 'archived',
}

export const EntitiesStatusNames = Object.freeze({
  [EntitiesStatus.Open]: 'Open',
  [EntitiesStatus.Resolved]: 'Resolved',
  [EntitiesStatus.Archived]: 'Archived',
});

// Audit lifecycle state, serialized as `auditStatus` on the audits records list.
export enum AuditStatus {
  InProgress = 'in_progress',
  Submitted = 'submitted',
}

export const AuditStatusNames = Object.freeze({
  [AuditStatus.InProgress]: 'In progress',
  [AuditStatus.Submitted]: 'Submitted',
});

/** Filter order, not lifecycle order: submitted is what people filter for, so it leads. */
export const AUDIT_STATUS_FILTER_ORDER: AuditStatus[] = [AuditStatus.Submitted, AuditStatus.InProgress];

export const MAX_API_KEY_LENGTH = 64;

export const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/heic'];

export const DROP_ZONE_EL_ID = 'drop-zone';

export const SCREEN_WIDTH_BREAKPOINTS = {
  SMALL_SCREEN: 1200,
  MEDIUM_SCREEN: 1400,
  LARGE_SCREEN: 1600,
};
